// A task's list, open — a wide panel over the screen it was opened from (the board stays
// behind it), the whole screen on a phone. The task's header, the bar and the filters,
// the rows, and the add box pinned to the bottom so the next name never needs a scroll.
//
// Opened from the strip on a task row, from the task dialog, and from the job screen and
// the lists index — always by TASK id, since the list has no life apart from its task.
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import he from '../../i18n/he';
import { taskListsApi, tasksApi } from '../../api';
import { useAlerts } from '../../context/AlertsContext';
import useIsPhone from '../../hooks/useIsPhone';
import AsyncBoundary, { ErrorText } from '../Loader';
import useListData from './useListData';
import ListPanelHead from './ListPanelHead';
import ListToolbar from './ListToolbar';
import ListTable from './ListTable';
import ListPhoneRows from './ListPhoneRows';
import ListAddBox from './ListAddBox';
import ListBulkBar from './ListBulkBar';
import ItemSheet from './ItemSheet';
import DefinitionModal from './DefinitionModal';
import CompleteTaskModal, { needsConfirm } from './CompleteTaskModal';
import { visibleItems } from './listView';
import ListGrouping from './ListGrouping';
import { groupRows, groupingOf, presetFor } from './listGroups';
import { printUrl } from './printUrl';

export default function ListPanel({ taskId, onClose, onChanged }) {
  const alerts = useAlerts();
  const changed = useCallback(() => { if (onChanged) onChanged(); }, [onChanged]);
  const { data, loading, error, failure, act, patchItem, reload } = useListData(taskId, changed);
  const phone = useIsPhone();
  const [stateId, setStateId] = useState(null);
  const [query, setQuery] = useState('');
  const [groupKey, setGroupKey] = useState(null); // one group shown alone, or all
  const [sheetFor, setSheetFor] = useState(null);
  const [defining, setDefining] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [selected, setSelected] = useState([]);

  const busy = !!sheetFor || defining || completing;
  useEffect(() => {
    if (busy) return undefined;
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [busy, onClose]);

  const setStatus = async (status) => {
    setCompleting(false);
    await act(() => tasksApi.setStatus(taskId, status));
    if (alerts) alerts.reload();
  };

  const onItem = (id, patch) => act(() => taskListsApi.updateItem(taskId, id, patch), patchItem(id, patch));
  const onRemove = (id) => {
    setSelected((sel) => sel.filter((x) => x !== id));
    return act(
      () => taskListsApi.removeItem(taskId, id),
      (cur) => ({ ...cur, list: { ...cur.list, items: cur.list.items.filter((i) => i.id !== id) } }),
    );
  };
  const onAdd = (items) => act(() => taskListsApi.addItems(taskId, items));
  const onBulk = (stateId) => {
    const ids = selected;
    setSelected([]);
    act(() => taskListsApi.setItemsState(taskId, ids, stateId), (cur) => ({
      ...cur,
      list: { ...cur.list, items: cur.list.items.map((i) => (ids.includes(i.id) ? { ...i, state_id: stateId } : i)) },
    }));
  };

  const task = data?.task;
  const list = data?.list;
  const grouping = list ? groupingOf(list) : null;
  const items = list ? visibleItems(list.items, { stateId, query, groupKey, grouping }) : [];
  const groups = list ? groupRows(items, list) : [];
  // How the list is organised is saved on it (079) — shown at once, confirmed by the reload.
  const onView = (view) => act(
    () => taskListsApi.setView(taskId, view),
    (cur) => ({ ...cur, list: { ...cur.list, view: { group_column_id: null, ...view } } }),
  );
  // The print page prints what is on screen: the same grouping, the same filters.
  const onPrint = () => window.open(printUrl(taskId, { groupKey, stateId, query }), '_blank', 'noopener');
  const sheetItem = list && sheetFor ? list.items.find((i) => i.id === sheetFor) : null;
  const rowsProps = { list, groups, locked: task?.locked };

  // The sheet and the dialogs are SIBLINGS of the overlay, not children: React events
  // bubble through the component tree, and a click on a dialog's backdrop must not also
  // reach the overlay and close the whole panel. The whole of it is portalled to <body>:
  // it opens from inside cards and dialogs, and an ancestor's transform would otherwise
  // pin a fixed overlay to that ancestor instead of the screen.
  return createPortal(
    <>
      <div className="list-panel-overlay" onClick={onClose} role="presentation">
        <section className="list-panel" role="dialog" aria-modal="true" aria-label={task?.title || he.lists.section} onClick={(e) => e.stopPropagation()}>
          <AsyncBoundary loading={loading} error={error}>
            {data && (
              <>
                <ListPanelHead
                  task={task}
                  phone={phone}
                  onClose={onClose}
                  onDefine={() => setDefining(true)}
                  onDone={() => (needsConfirm(data.summary) ? setCompleting(true) : setStatus('done'))}
                  onReopen={() => setStatus('open')}
                  onPrint={onPrint}
                />
                <ListToolbar summary={data.summary} stateId={stateId} onState={setStateId} query={query} onQuery={setQuery} />
                <ListGrouping list={list} onView={onView} groupKey={groupKey} onGroup={setGroupKey} />
                {failure && <ErrorText error={failure} />}
                {selected.length > 0 && !phone && !task.locked && (
                  <ListBulkBar states={list.states} count={selected.length} onState={onBulk} onClear={() => setSelected([])} />
                )}
                <div className="list-body">
                  {items.length === 0 && list.items.length > 0 && <p className="muted small-note">{he.lists.noMatch}</p>}
                  {phone
                    ? <ListPhoneRows {...rowsProps} onOpen={setSheetFor} />
                    : <ListTable {...rowsProps} onItem={onItem} onRemove={onRemove} selected={selected} onSelect={setSelected} />}
                </div>
                {!task.locked && (
                  <div className="list-foot"><ListAddBox onAdd={onAdd} columns={list.columns} autoFocus={list.items.length === 0} preset={presetFor(list, groupKey || (grouping.mode === 'state' && stateId ? `s:${stateId}` : null))} /></div>
                )}
              </>
            )}
          </AsyncBoundary>
        </section>
      </div>

      {sheetItem && !task.locked && (
        <ItemSheet list={list} item={sheetItem} onItem={onItem} onRemove={onRemove} onClose={() => setSheetFor(null)} />
      )}
      {defining && (
        <DefinitionModal taskId={taskId} list={list} onClose={() => setDefining(false)} onSaved={() => { setDefining(false); reload(); changed(); }} />
      )}
      {completing && (
        <CompleteTaskModal title={task.title} summary={data.summary} onClose={() => setCompleting(false)} onConfirm={() => setStatus('done')} />
      )}
    </>,
    document.body,
  );
}
